import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Web3 from 'web3';
import { AbstractProvider } from 'web3-core';
import { getChainName } from '../../utils/utils';
import useAlert from '../alert';
import InstallMetamaskDialog from './metamaskDialog';

export enum MetamaskStatuses {
  INACTIVE,
  WRONG_CHAIN,
  NORMAL
}

type ChainData = {
  status: MetamaskStatuses;
  chainName: string;
};

type ConnectResult = {
  web3: Web3;
  publicAddress: string;
};

interface IWeb3 {
  init: () => Promise<void>;
  connect: (skipIfEnabled?: boolean) => Promise<ConnectResult>;
  getWeb3: () => Web3;
  chainData: ChainData;
  handleMetamaskClick: (
    callback: () => any,
    doWhat: string,
    anyCase?: () => any
  ) => void;
  openInstallDialog: () => void;
}

// @ts-ignore
const Web3Context = createContext<IWeb3>();

interface Web3ProviderProps {
  children: React.ReactNode;
}

export const Web3ContextProvider = ({ children }: Web3ProviderProps) => {
  const alert = useAlert();
  const web3 = useRef<Web3 | undefined>(undefined);
  const chainHandler = useRef<((chainId: string) => void) | undefined>(
    undefined
  );
  const actualChainId = useRef<string | undefined>(undefined);
  const [chainId, setChainId] = useState<string | undefined>(undefined);
  const [installMMOpened, setInstallMMOpened] = useState(false);

  useEffect(() => {
    actualChainId.current = chainId;
  }, [chainId]);

  const init = async () => {
    if (!window.ethereum) return;
    //detecting current chain
    const currentChainId = (await window.ethereum.request({
      method: 'eth_chainId'
    })) as string;
    setChainId(currentChainId);
    actualChainId.current = currentChainId;
    injectChainListener();
  };

  const injectChainListener = () => {
    //setting up a listener on chain change (removing old one as well)
    if (chainHandler.current) {
      window.ethereum.removeListener('chainChanged', chainHandler.current);
    }
    const handler = (chainId: any) => setChainId(chainId);
    chainHandler.current = handler;
    window.ethereum.on('chainChanged', handler);
  };

  const chainData = useMemo<ChainData>(() => {
    if (!chainId)
      return {
        status: MetamaskStatuses.INACTIVE,
        chainName: ''
      };
    if (chainId !== process.env.REACT_APP_CHAINID) {
      return {
        status: MetamaskStatuses.WRONG_CHAIN,
        chainName: getChainName(chainId)
      };
    }
    return {
      status: MetamaskStatuses.NORMAL,
      chainName: getChainName(chainId)
    };
  }, [chainId]);

  const switchNetwork = (): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: process.env.REACT_APP_CHAINID }]
        });
        setChainId(process.env.REACT_APP_CHAINID);
        resolve();
      } catch (err: any) {
        if (err?.code === 4902 || err?.code === -32603) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: process.env.REACT_APP_CHAINID,
                  chainName: process.env.REACT_APP_CHAIN_NAME,
                  nativeCurrency: {
                    name: process.env.REACT_APP_CURRENCY_SYMBOL,
                    symbol: process.env.REACT_APP_CURRENCY_SYMBOL,
                    decimals: 18
                  },
                  rpcUrls: [process.env.REACT_APP_RPC_URL],
                  blockExplorerUrls: [
                    process.env.REACT_APP_NETWORK_EXPLORER_URL
                  ]
                }
              ]
            });
            setChainId(process.env.REACT_APP_CHAINID);
            resolve();
          } catch (err) {
            reject(err);
          }
        } else {
          reject(err);
        }
      }
    });

  const enableWeb3 = (skipIfEnabled = true): Promise<Web3 | undefined> => {
    return new Promise(async (resolve) => {
      const unlocked = await window.ethereum?._metamask?.isUnlocked();
      if (web3.current && unlocked && skipIfEnabled)
        return resolve(web3.current);
      try {
        await window.ethereum.request({
          method: 'eth_requestAccounts'
        });
        web3.current = new Web3(window.ethereum as AbstractProvider);
        resolve(web3.current);
      } catch (error) {
        resolve(undefined);
      }
    });
  };

  const connect = (skipIfEnabled: boolean = true): Promise<ConnectResult> => {
    return new Promise(async (resolve, reject) => {
      if (!window.ethereum) {
        return reject({
          type: 'install',
          message: 'Pls install MetaMask'
        });
      }
      if (
        !actualChainId.current ||
        actualChainId.current !== process.env.REACT_APP_CHAINID
      )
        return reject({
          type: 'chain'
        });
      const result = await enableWeb3(skipIfEnabled);
      if (!result)
        return reject({
          type: 'common',
          message: 'Please allow MetaMask access'
        });
      await result.eth.getAccounts();
      const coinbase = await result.eth.getCoinbase();
      if (!coinbase)
        return reject({
          type: 'common',
          message: 'Pls activate Metamask first'
        });
      resolve({ web3: result, publicAddress: coinbase.toLowerCase() });
    });
  };

  // @ts-ignore
  const getWeb3 = (): Web3 => web3.current;

  const handleMetamaskClick = (
    callback: () => any,
    doWhat = 'proceed',
    anyCase?: () => any
  ) => {
    switch (chainData.status) {
      case MetamaskStatuses.INACTIVE:
        if (anyCase) anyCase();
        return setInstallMMOpened(true);
      case MetamaskStatuses.WRONG_CHAIN: {
        switchNetwork()
          .then(() => setTimeout(callback, 500))
          .catch(() => {
            alert.showError(
              `To ${doWhat} please switch to ${getChainName(
                process.env.REACT_APP_CHAINID
              )}`
            );
          })
          .finally(() => {
            if (anyCase) anyCase();
          });
        return;
      }
      default: {
        callback();
        if (anyCase) anyCase();
      }
    }
  };

  return (
    <Web3Context.Provider
      value={{
        init,
        connect,
        getWeb3,
        chainData,
        handleMetamaskClick,
        openInstallDialog: () => setInstallMMOpened(true)
      }}
    >
      {children}
      <InstallMetamaskDialog
        opened={installMMOpened}
        onClose={() => setInstallMMOpened(false)}
      />
    </Web3Context.Provider>
  );
};

const useWeb3 = () => useContext(Web3Context);

export default useWeb3;
